import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CustomAuthServiceService } from '../auth/custom-auth-service.service';

// Terminal `**` route. Without it Angular throws NG04002 on any URL that matches
// nothing (a link to a renamed route, a stale bookmark, a typo) and leaves the
// router-outlet empty — a white screen with no way out but a manual refresh.
@Component({
  selector: 'ngx-not-found-redirect',
  template: `
    <div *ngIf="showNotFound"
         style="font-family: Arial, Helvetica, sans-serif; max-width: 480px; margin: 15vh auto 0;
                padding: 0 24px; text-align: center; color: #222;">
      <h1 style="font-size: 22px;">Page not found</h1>
      <p style="font-size: 15px; line-height: 1.5;">That link points to a page that no longer exists.</p>
      <a href="/auth/login" style="font-size: 15px;">Go to sign in</a>
    </div>
  `,
})
export class NotFoundRedirectComponent implements OnInit, OnDestroy {

  showNotFound = false;

  // Targets this instance already tried. Several role landing URLs in
  // landingUrlForRole() have no route (e.g. 'underwriting'), so a redirect can land
  // straight back here; without this it would bounce forever.
  private readonly attempted = new Set<string>();
  private sub: Subscription;

  constructor(private router: Router, private auth: CustomAuthServiceService) { }

  ngOnInit(): void {
    // Angular reuses this component across two unmatched URLs, so ngOnInit alone
    // would not run for the second one — react to the navigation instead.
    this.sub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.resolve());
    this.resolve();
  }

  ngOnDestroy(): void {
    if (this.sub) { this.sub.unsubscribe(); }
  }

  private resolve(): void {
    if (!this.auth.isLoggedIn()) {
      this.router.navigate(['/auth/login']);
      return;
    }

    const target = this.auth.resolveLandingUrl();
    if (!target || this.attempted.has(target) || this.isCurrentUrl(target)) {
      this.showNotFound = true;
      return;
    }

    this.attempted.add(target);
    this.router.navigateByUrl(target);
  }

  private isCurrentUrl(target: string): boolean {
    const normalize = (url: string) => ('/' + url).replace(/\/+/g, '/').split('?')[0];
    return normalize(target) === normalize(this.router.url);
  }
}
